import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
    DashboardTitle,
    DashboardWrapper,
    SearchDebounce,
} from '../../components';

import * as actionCreator from "../../actions";
import LicenseModel from '../../models/License';
import ScreenItemSkeleton from '../../components/ScreenItem/ScreenItemSkeleton';
import InfiniteScroll from 'react-infinite-scroller';
import { IconLoader } from '../../assets/icons/IconLoader';
import LicenseItem from '../../components/LicenseItem';

interface LicensesProps {};

export interface LicenseInputs {
    name?: string | null;
}

const Licenses: React.FC<LicensesProps> = (props: LicensesProps) => {

    const [t] = useTranslation();

    const dispatch = useDispatch();

    const [search, setSearch] = useState<string>('');
    
    const licenseResponse = useSelector( (store: any) => store.licenseReducer );
    
    const isLoading = licenseResponse?.isLoading
    const pagination: any = licenseResponse?.data;
    const data: any = pagination?.data ?? [];

    useEffect(() => {
    
        _fetchLicenses(1);
        
        return () => {
            dispatch( actionCreator.licenseCancelRequest() );
        }

    }, []);

    const _fetchLicenses = (page?: number) => {
        dispatch( actionCreator.licenseCancelRequest() );
        dispatch( actionCreator.licenseFetch(page, search) );
    }

    const _renderSkeletonLoading = () => {
        return Array.from({length: 15}, (_, i) => i + 1).map((item: any, id: number) => {
            return (
                <div key={id} className="mb-3">
                    <ScreenItemSkeleton />
                </div>
            );
        });
    }

    const _renderLicenses = () => {
    
        if(isLoading) {
            return _renderSkeletonLoading();
        }

        let licenses: LicenseModel[] = data?.map((license: LicenseModel, index: number) => {
            return(
                <LicenseItem
                    key={index}
                    license={license}
                    openModal={() => {}}
                />
            )
        }) ?? [];

        if(licenses.length === 0) {
            return(
                <div className="row">
                    <div className="col-12 col-sm-6 col-lg-4 col-xl-3 mb-4">
                        {t('No Result')}
                    </div>
                </div>
            )
        }

        return(
            <InfiniteScroll
                pageStart={pagination?.from}
                loadMore={() => {
                    _fetchLicenses(pagination?.current_page ? pagination?.current_page + 1 : 1)
                }}
                hasMore={pagination?.next_page_url !== null}
                loader={
                    <div key="loading" className="d-flex justify-content-center">
                        <IconLoader class='stroke-gray-light' styles={{ width: '50px', height: '50px' }} />
                    </div>
                }
            >
                {licenses}
            </InfiniteScroll>
        );

    }

    return(
            <DashboardWrapper>
                <div className="row align-items-center mb-1">
                    <div className="col-12">
                        <DashboardTitle 
                            title={t('Licenses')}
                            isLoading={isLoading}
                            withRefreshIcon={!isLoading}
                            refreshIconCallBack={() => {
                                _fetchLicenses(1);
                            }}
                        />
                    </div>
                </div>
                <div className="row align-items-center mb-3">
                    <div className="col">
                        <SearchDebounce
                            placeholder={`${t('Name')}`}
                            borderColor="transparent"
                            onChange={(text: string) => setSearch(text)}
                            debounceFinish={(text: string) => _fetchLicenses(1)}
                        />
                    </div>
                </div>
    
                <div className="mt-3">
                    {_renderLicenses()}
                </div>
                
            </DashboardWrapper>
        );

};

export default React.memo(Licenses);
